import { useInterval } from 'ahooks'
import lodash from 'lodash'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'

import { t } from 'services/Translation'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE

const FIRE_TIME = 2 * MINUTE

const UPDATE_INTERVAL = 100
const getSeconds = (time?: number | null) => lodash.round(time || Date.now(), -3)

interface ITimerOptions {
  onFinish: () => void
  time: number
  startTime?: number | null
  stop?: boolean
  questionsDone?: number
}

export const formatDuration = (timeMs: number) => {
  const hours = Math.floor(timeMs / HOUR)
  const minutes = Math.floor((timeMs % HOUR) / MINUTE)
  const seconds = Math.floor((timeMs % MINUTE) / SECOND)

  return [
    hours > 0 && t('common.time.hour', { count: hours }),
    minutes > 0 && t('common.time.minute', { count: minutes }),
    seconds > 0 && t('common.time.second', { count: seconds }),
  ]
    .filter(Boolean)
    .join(' ')
}

export const useTestTimer = ({ onFinish, startTime, time, stop }: ITimerOptions) => {
  const [now, setNow] = useState(getSeconds())

  const deadline = getSeconds(startTime) + time * SECOND
  const left = stop ? time * SECOND : deadline - getSeconds()
  const format = left <= HOUR ? 'mm:ss' : 'HH:mm:ss'

  useInterval(() => !stop && now !== getSeconds() && setNow(getSeconds()), UPDATE_INTERVAL)

  useEffect(() => {
    if (now >= deadline) {
      onFinish()
    }
  }, [now, deadline, onFinish])

  return {
    left: moment.utc(left).format(format),
    isMinuteRound: left % MINUTE === 0,
    minutesLeft: Math.ceil(left / MINUTE),
    leftMs: left,
    isFire: left <= FIRE_TIME,
    isTimeUp: now >= deadline,
  }
}

export const useAccessibleTimer = ({
  onFinish,
  startTime,
  time,
  stop,
  questionsDone,
}: ITimerOptions) => {
  const { left, minutesLeft, isFire, isTimeUp, leftMs } = useTestTimer({ onFinish, startTime, time, stop })
  const leftmsRef = useRef(leftMs)
  leftmsRef.current = leftMs
  const [srMessage, setSrMessage] = useState('')

  useEffect(() => {
    if (questionsDone && questionsDone > 0) {
      setSrMessage(`${formatDuration(leftmsRef.current)} remaining.`)
      return
    }

    if (isFire) {
      setSrMessage(`Low time! ${formatDuration(minutesLeft * MINUTE)} remaining.`)
      return
    }

    if (isTimeUp) {
      setSrMessage('Time is up!')
      return
    }

    setSrMessage(`${formatDuration(minutesLeft * MINUTE)} remaining.`)
  }, [isFire, minutesLeft, isTimeUp, questionsDone])

  return { left, srMessage, isFire }
}
